import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MafiaSimulator from '../simulator/MafiaSimulator';
import ReadOnlySimulator from '../quiz/ReadOnlySimulator';
import {
  getQuizById,
  getQuizQuestions,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  type Quiz,
  type QuizQuestion,
  type QuestionType,
  type QuestionOption
} from '../../services/quizService';
import { PlayerState } from '../simulator/PlayerCircle';

interface QuizQuestionEditorProps {
  quizId: string;
  onBack: () => void;
}

const QuizQuestionEditor: React.FC<QuizQuestionEditorProps> = ({ quizId, onBack }) => {
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [simulatorDialogOpen, setSimulatorDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuizQuestion | null>(null);
  const [questionText, setQuestionText] = useState('');
  const [acceptedAnswer, setAcceptedAnswer] = useState('');
  const [questionType, setQuestionType] = useState<QuestionType>('free_text');
  const [options, setOptions] = useState<QuestionOption[]>([]);
  const [newOptionText, setNewOptionText] = useState('');
  const [includeSimulator, setIncludeSimulator] = useState(false);
  const [simulatorState, setSimulatorState] = useState<PlayerState[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadQuizAndQuestions();
  }, [quizId]);

  const loadQuizAndQuestions = async () => {
    try {
      setLoading(true);
      const [quizData, questionsData] = await Promise.all([
        getQuizById(quizId),
        getQuizQuestions(quizId)
      ]);
      setQuiz(quizData);
      setQuestions(questionsData);
      setError(null);
    } catch (err) {
      console.error('Error loading quiz and questions:', err);
      setError('Failed to load quiz data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setQuestionText('');
    setAcceptedAnswer('');
    setQuestionType('free_text');
    setOptions([]);
    setNewOptionText('');
    setIncludeSimulator(false);
    setSimulatorState([]);
    setEditDialogOpen(true);
  };

  const handleEditQuestion = (question: QuizQuestion) => {
    setEditingQuestion(question);
    setQuestionText(question.question_text);
    setAcceptedAnswer(question.accepted_answer);
    setQuestionType(question.question_type);
    setOptions(question.options || []);
    setNewOptionText('');
    setIncludeSimulator(!!question.simulator_state && question.simulator_state.length > 0);
    setSimulatorState(question.simulator_state || []);
    setEditDialogOpen(true);
  };

  const handleConfigureSimulator = () => {
    setEditDialogOpen(false);
    setSimulatorDialogOpen(true);
  };

  const handleSimulatorSave = (states: PlayerState[]) => {
    setSimulatorState(states);
    setSimulatorDialogOpen(false);
    setEditDialogOpen(true);
  };

  const handleAddOption = () => {
    if (!newOptionText.trim()) return;

    const newOption: QuestionOption = {
      id: `option-${Date.now()}`,
      text: newOptionText.trim()
    };

    setOptions([...options, newOption]);
    setNewOptionText('');
  };

  const handleDeleteOption = (optionId: string) => {
    setOptions(options.filter(opt => opt.id !== optionId));
    // If deleted option was the correct answer, clear accepted_answer
    if (acceptedAnswer === optionId) {
      setAcceptedAnswer('');
    }
  };

  const handleToggleCorrectAnswer = (optionId: string) => {
    // For now, support single correct answer (can extend to multiple later)
    setAcceptedAnswer(acceptedAnswer === optionId ? '' : optionId);
  };

  const handleSaveQuestion = async () => {
    if (!questionText.trim()) {
      setError('Question text is required');
      return;
    }

    if (questionType === 'free_text' && !acceptedAnswer.trim()) {
      setError('Accepted answer is required for free text questions');
      return;
    }

    if (questionType === 'multiple_choice') {
      if (options.length < 2) {
        setError('Multiple choice questions must have at least 2 options');
        return;
      }
      if (!acceptedAnswer) {
        setError('Please select the correct answer');
        return;
      }
    }

    try {
      setSaving(true);
      const simulatorData = includeSimulator && simulatorState.length > 0 ? simulatorState : undefined;

      if (editingQuestion) {
        // Update existing question
        await updateQuestion(editingQuestion.id, {
          question_text: questionText,
          accepted_answer: acceptedAnswer,
          question_type: questionType,
          options: questionType === 'multiple_choice' ? options : undefined,
          simulator_state: simulatorData
        });
      } else {
        // Create new question
        const nextOrderNum = questions.length > 0
          ? Math.max(...questions.map(q => q.order_num)) + 1
          : 1;
        await createQuestion(
          quizId,
          questionText,
          acceptedAnswer,
          nextOrderNum,
          questionType,
          questionType === 'multiple_choice' ? options : undefined,
          simulatorData
        );
      }

      setEditDialogOpen(false);
      await loadQuizAndQuestions();
      setError(null);
    } catch (err) {
      console.error('Error saving question:', err);
      setError('Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await deleteQuestion(questionId);
      await loadQuizAndQuestions();
      setError(null);
    } catch (err) {
      console.error('Error deleting question:', err);
      setError('Failed to delete question');
    }
  };

  const handleReorderQuestion = async (question: QuizQuestion, direction: 'up' | 'down') => {
    const currentIndex = questions.findIndex(q => q.id === question.id);
    if (currentIndex === -1) return;

    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === questions.length - 1) return;

    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const otherQuestion = questions[swapIndex];

    try {
      // Swap order_num values
      await Promise.all([
        updateQuestion(question.id, { order_num: otherQuestion.order_num }),
        updateQuestion(otherQuestion.id, { order_num: question.order_num })
      ]);

      await loadQuizAndQuestions();
      setError(null);
    } catch (err) {
      console.error('Error reordering question:', err);
      setError('Failed to reorder question');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!quiz) {
    return (
      <Box>
        <Alert severity="error">Quiz not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mt: 2 }}>
          Back to Quizzes
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            {quiz.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Edit Questions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddQuestion}
        >
          Add Question
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="60px"><strong>#</strong></TableCell>
              <TableCell><strong>Question</strong></TableCell>
              <TableCell width="120px"><strong>Type</strong></TableCell>
              <TableCell><strong>Answer</strong></TableCell>
              <TableCell width="100px" align="center"><strong>Simulator</strong></TableCell>
              <TableCell width="180px" align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {questions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No questions yet. Click "Add Question" to get started!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              questions.map((question, index) => (
                <TableRow key={question.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      {question.question_text}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={question.question_type === 'multiple_choice' ? 'Multiple Choice' : 'Free Text'}
                      color={question.question_type === 'multiple_choice' ? 'secondary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {question.question_type === 'multiple_choice' ? (
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
                        {question.options?.find(opt => opt.id === question.accepted_answer)?.text || '-'}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
                        {question.accepted_answer}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {question.simulator_state && question.simulator_state.length > 0 ? (
                      <Chip label="Yes" color="primary" size="small" />
                    ) : (
                      <Chip label="No" variant="outlined" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleReorderQuestion(question, 'up')}
                      disabled={index === 0}
                      title="Move Up"
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleReorderQuestion(question, 'down')}
                      disabled={index === questions.length - 1}
                      title="Move Down"
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditQuestion(question)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteQuestion(question.id)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Create Question Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !saving && setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingQuestion ? 'Edit Question' : 'Add New Question'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Question Text"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">Question Type</FormLabel>
            <RadioGroup
              row
              value={questionType}
              onChange={(e) => {
                setQuestionType(e.target.value as QuestionType);
                // Clear options and accepted answer when switching types
                if (e.target.value === 'free_text') {
                  setOptions([]);
                  setAcceptedAnswer('');
                }
              }}
            >
              <FormControlLabel value="free_text" control={<Radio />} label="Free Text" />
              <FormControlLabel value="multiple_choice" control={<Radio />} label="Multiple Choice" />
            </RadioGroup>
          </FormControl>

          {questionType === 'free_text' ? (
            <TextField
              margin="dense"
              label="Accepted Answer"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={2}
              value={acceptedAnswer}
              onChange={(e) => setAcceptedAnswer(e.target.value)}
              helperText="This is the reference answer for grading purposes"
              sx={{ mb: 2 }}
            />
          ) : (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Answer Options
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter option text..."
                  value={newOptionText}
                  onChange={(e) => setNewOptionText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddOption}
                  disabled={!newOptionText.trim()}
                >
                  Add
                </Button>
              </Box>

              {options.length > 0 ? (
                <List dense>
                  {options.map((option) => (
                    <ListItem
                      key={option.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: acceptedAnswer === option.id ? 'success.light' : 'background.paper'
                      }}
                    >
                      <Checkbox
                        edge="start"
                        checked={acceptedAnswer === option.id}
                        onChange={() => handleToggleCorrectAnswer(option.id)}
                        color="success"
                      />
                      <ListItemText
                        primary={option.text}
                        primaryTypographyProps={{
                          sx: { fontWeight: acceptedAnswer === option.id ? 'bold' : 'normal' }
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDeleteOption(option.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                  No options added yet. Add at least 2 options.
                </Typography>
              )}

              {options.length > 0 && !acceptedAnswer && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please check the box next to the correct answer
                </Alert>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={includeSimulator}
                onChange={(e) => setIncludeSimulator(e.target.checked)}
              />
            }
            label="Include Mafia Simulator State"
          />

          {includeSimulator && (
            <Box sx={{ mt: 2 }}>
              {simulatorState.length > 0 ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Current Simulator Configuration:
                  </Typography>
                  <Box sx={{ my: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <ReadOnlySimulator playerStates={simulatorState} />
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={handleConfigureSimulator}
                    fullWidth
                  >
                    Reconfigure Simulator
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleConfigureSimulator}
                  fullWidth
                >
                  Configure Simulator
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveQuestion} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save Question'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Simulator Configuration Dialog */}
      <Dialog
        open={simulatorDialogOpen}
        onClose={() => setSimulatorDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Configure Mafia Simulator State</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
            Configure the game state that will be shown with this question. Set up the players, roles, and game state, then click "Save State" to capture it.
          </Typography>
          <MafiaSimulator
            initialStates={simulatorState.length > 0 ? simulatorState : undefined}
            onSave={handleSimulatorSave}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimulatorDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuizQuestionEditor;
